import { Directive, ElementRef, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Scroll-reveal trigger. Add `appReveal` to a container; mark the elements that should
 * animate with a variant class (`r-up`, `r-left`, `r-right`, `r-scale`, `r-drop`) and an
 * optional per-item `--reveal-delay` for stagger. The actual motion lives in global CSS,
 * gated behind `@media (prefers-reduced-motion: no-preference)`.
 *
 * SSR-safe: does nothing on the server and content stays fully visible. On the browser it
 * arms `.reveal-ready`, then a single shared IntersectionObserver adds `.is-visible` once the
 * container scrolls into view. Reduced-motion users never get armed, so they see static content.
 * Class toggles are direct DOM writes, so this is fine under zoneless change detection.
 */
@Directive({ selector: '[appReveal]' })
export class RevealDirective implements OnInit, OnDestroy {
  private static observer: IntersectionObserver | null = null;
  private readonly el: HTMLElement;

  constructor(@Inject(PLATFORM_ID) private platformId: Object, ref: ElementRef<HTMLElement>) {
    this.el = ref.nativeElement;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const reduce = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;

    this.el.classList.add('reveal-ready');
    RevealDirective.ensureObserver().observe(this.el);
  }

  ngOnDestroy(): void {
    RevealDirective.observer?.unobserve(this.el);
  }

  private static ensureObserver(): IntersectionObserver {
    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer!.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );
    }
    return this.observer;
  }
}
